import { useEffect, useRef } from 'react';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-in');
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

export default function Lessons() {
  const headerRef = useScrollReveal();
  const card0Ref = useScrollReveal();
  const card1Ref = useScrollReveal();
  const card2Ref = useScrollReveal();
  const cardRefs = [card0Ref, card1Ref, card2Ref];

  const lessons = [
    {
      id: 1,
      title: 'Surf Lessons',
      image: 'https://public.readdy.ai/ai/img_res/c9f860480deaa7e87dd6bb1de456dd50.jpg',
      features: ['Beginner to Advanced', 'Small Groups (Max 6)', 'All Equipment Included'],
      price: '35',
      description: 'Learn to surf with certified instructors in perfect conditions'
    },
    {
      id: 2,
      title: 'Kitesurf Lessons',
      image: 'https://readdy.ai/api/search-image?query=Kitesurfer%20performing%20aerial%20trick%20with%20colorful%20kite%20against%20blue%20sky%20in%20Essaouira%20Morocco%2C%20dynamic%20action%20shot%20showing%20freedom%20and%20excitement%20of%20kitesurfing%2C%20vibrant%20turquoise%20ocean%20water%20with%20white%20sand%20beach%2C%20professional%20extreme%20water%20sports%20photography%20with%20dramatic%20movement%20and%20energy&width=800&height=1000&seq=kitesurf-action-aerial-001&orientation=portrait',
      features: ['IKO Certified Training', 'Latest Equipment', 'Perfect Wind Conditions'],
      price: '50',
      description: 'Master kitesurfing in one of the world\'s best spots'
    },
    {
      id: 3,
      title: 'Private Sessions',
      image: 'https://readdy.ai/api/search-image?query=One-on-one%20private%20surf%20coaching%20session%20on%20beautiful%20beach%20in%20Essaouira%20Morocco%2C%20personal%20instructor%20helping%20student%20with%20surfboard%20technique%2C%20intimate%20professional%20training%20atmosphere%20with%20pristine%20turquoise%20waves%20and%20golden%20sand%2C%20high-quality%20outdoor%20sports%20education%20photography%20showing%20dedication%20and%20progress&width=800&height=1000&seq=private-surf-coaching-001&orientation=portrait',
      features: ['1-on-1 Coaching', 'Flexible Schedule', 'Rapid Progression'],
      price: '80',
      description: 'Personalized instruction tailored to your goals'
    }
  ];

  return (
    <section id="lessons" className="py-24 bg-sand-100">
      <style>{`
        .reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .reveal.animate-in { opacity: 1; transform: translateY(0); }
        .reveal-delay-1 { transition-delay: 0.1s; }
        .reveal-delay-2 { transition-delay: 0.22s; }
        .reveal-delay-3 { transition-delay: 0.34s; }
      `}</style>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div ref={headerRef} className="reveal text-center mb-16">
          <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">Nos Activités</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choisissez Votre Vague
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto">
            Du débutant au rider confirmé, nous avons la formule idéale pour votre niveau
          </p>
        </div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {lessons.map((lesson, index) => (
            <div
              key={lesson.id}
              ref={cardRefs[index]}
              className={`reveal reveal-delay-${index + 1} group bg-sand-50 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1 cursor-pointer border border-sand-200`}
            >
              {/* Image */}
              <div className="relative h-72 w-full overflow-hidden">
                <img
                  src={lesson.image}
                  alt={lesson.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{lesson.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{lesson.description}</p>
                <ul className="space-y-2 mb-6">
                  {lesson.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-base w-5 h-5 flex items-center justify-center"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-3xl font-bold text-teal-600">€{lesson.price}</span>
                    <span className="text-sm text-gray-400 ml-1">/ pers.</span>
                  </div>
                </div>
                <button className="w-full mt-4 bg-gray-900 text-white py-3 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-teal-600 transition-all cursor-pointer">
                  Réserver
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
